import axios, {AxiosResponse} from 'axios'

import {
  TApiPricingRequest,
  TApiPricingResponse,
  TFranchisePartenershipRequest,
  TFranchiseRequest,
  TFranchiseResponse,
  TFranchiseSyncRequest,
  TInvoiceConfigRequest,
  TInvoiceConfigResponse,
  TStarRequest,
  TStarResponse,
} from '../../../coreGlobal/models/franchise/franchise'
import {Response} from '../../../_metronic/helpers'
import {OwnersFranchiseRequest} from '../../../coreGlobal/models/machines/machine'
import {IPersonResponse} from '../../../coreGlobal/models/person'
import {
  AdminFranchisesQueryResponse,
  FranchiseMachine,
  FranchiseMachinesRequest,
  FranchiseProduct,
  FranchiseProductsRequest,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL + '/api'
const FRANCHISE_URL = `${API_URL}/admin/franchises`
const PEOPLE_URL = `${API_URL}/admin/people`

const getFranchises = (query: string): Promise<AdminFranchisesQueryResponse> => {
  return axios
    .get(`${FRANCHISE_URL}?${query}&include=person.juridicalPerson`)
    .then((d: AxiosResponse<AdminFranchisesQueryResponse>) => d.data)
}

// Busca de sócios sem limitar às franquias do usuário logado
const findPeople = (query: string): Promise<IPersonResponse[]> => {
  return axios
    .get(`${PEOPLE_URL}/find${query}`)
    .then((response: AxiosResponse<Response<IPersonResponse[]>>) => response.data.data ?? [])
}

// Busca a pessoa pelo documento sem limitar às franquias do usuário logado
const findPersonByDocument = (document: string): Promise<IPersonResponse> => {
  return axios
    .get(`${PEOPLE_URL}/findByDocument`, {params: {document}})
    .then((response: AxiosResponse<Response<IPersonResponse>>) => response.data.data as IPersonResponse)
}

const createFranchise = (data: TFranchiseRequest) => {
  return axios
    .post(FRANCHISE_URL, data)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const updateFranchise = (franchiseId: string | number, data: TFranchiseRequest) => {
  return axios
    .put(`${FRANCHISE_URL}/${franchiseId}`, data)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const updateFranchiseSync = (franchiseId: string | number, data: TFranchiseSyncRequest) => {
  return axios
    .put(`${FRANCHISE_URL}/${franchiseId}/config`, data)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const updateFranchiseStar = (franchiseId: string | number, data: TStarRequest[]) => {
  return axios
    .post(`${FRANCHISE_URL}/${franchiseId}/stars`, data)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const updateApiPricing = (franchiseId: string | number, data: TApiPricingRequest) => {
  return axios
    .put(`${FRANCHISE_URL}/${franchiseId}/api-pricing`, data)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const updateInvoiceConfig = (franchiseId: string | number, data: TInvoiceConfigRequest) => {
  return axios
    .put(`${FRANCHISE_URL}/${franchiseId}/invoice-config`, data)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const updateFranchisePartnership = (
  franchiseId: string | number,
  data: TFranchisePartenershipRequest
) => {
  return axios
    .post(`${FRANCHISE_URL}/${franchiseId}/partnerships`, data)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const getFranchiseById = (id: string): Promise<TFranchiseResponse> => {
  return axios
    .get(`${FRANCHISE_URL}/${id}`)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const getStarsById = (id: string): Promise<TStarResponse> => {
  return axios
    .get(`${FRANCHISE_URL}/${id}/stars`)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const getInvoiceConfigId = (id: string): Promise<TInvoiceConfigResponse> => {
  return axios
    .get(`${FRANCHISE_URL}/${id}/invoice-config`)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const getApiPricingId = (id: string): Promise<TApiPricingResponse> => {
  return axios
    .get(`${FRANCHISE_URL}/${id}/api-pricing`)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const validateOwnerPerson = (document: string, birthDate?: string) => {
  return axios
    .get(`${PEOPLE_URL}/validatePerson`, {params: {document, birthdate: birthDate || undefined}})
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const updateOwners = (ownerId: number, data: OwnersFranchiseRequest) => {
  return axios
    .post(`${FRANCHISE_URL}/${ownerId}/owners`, data)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

// Cadastra um novo sócio, que também entra como cliente da franquia
const createOwner = (franchiseId: number, data: FormData) => {
  return axios
    .post(`${FRANCHISE_URL}/${franchiseId}/owners/person`, data)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const syncOldUsersByFranchiseId = (franchiseId: number) => {
  return axios
    .post(`${FRANCHISE_URL}/${franchiseId}/config/sync-old-users`)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const syncOldClientsByFranchiseId = (franchiseId: number) => {
  return axios
    .post(`${FRANCHISE_URL}/${franchiseId}/config/sync-old-clients`)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const syncOldSalesByFranchiseId = (franchiseId: number) => {
  return axios
    .post(`${FRANCHISE_URL}/${franchiseId}/config/sync-old-sales`)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const syncOldSchedulesByFranchiseId = (franchiseId: number) => {
  return axios
    .post(`${FRANCHISE_URL}/${franchiseId}/config/sync-old-events`)
    .then((response: any) => response.data)
    .then((response: any) => response.data)
}

const getFranchiseProducts = (franchiseId: number): Promise<FranchiseProduct[]> => {
  return axios
    .get(`${FRANCHISE_URL}/${franchiseId}/products`)
    .then(
      (response: AxiosResponse<Response<FranchiseProduct[]>>) =>
        response.data.data as FranchiseProduct[]
    )
}

const syncFranchiseProducts = (franchiseId: number, data: FranchiseProductsRequest) => {
  return axios
    .put(`${FRANCHISE_URL}/${franchiseId}/products`, data)
    .then((response: any) => response.data)
}

const getFranchiseMachines = (franchiseId: number): Promise<FranchiseMachine[]> => {
  return axios
    .get(`${FRANCHISE_URL}/${franchiseId}/machine-units`)
    .then(
      (response: AxiosResponse<Response<FranchiseMachine[]>>) =>
        response.data.data as FranchiseMachine[]
    )
}

const syncFranchiseMachines = (franchiseId: number, data: FranchiseMachinesRequest) => {
  return axios
    .put(`${FRANCHISE_URL}/${franchiseId}/machine-units`, data)
    .then((response: any) => response.data)
}

export {
  createFranchise,
  updateFranchise,
  getFranchiseById,
  getFranchises,
  findPeople,
  updateOwners,
  createOwner,
  validateOwnerPerson,
  findPersonByDocument,
  syncOldUsersByFranchiseId,
  syncOldClientsByFranchiseId,
  updateFranchiseSync,
  syncOldSalesByFranchiseId,
  syncOldSchedulesByFranchiseId,
  updateFranchisePartnership,
  getStarsById,
  updateFranchiseStar,
  getFranchiseProducts,
  syncFranchiseProducts,
  getFranchiseMachines,
  syncFranchiseMachines,
  getInvoiceConfigId,
  getApiPricingId,
  updateApiPricing,
  updateInvoiceConfig,
}
